import { useId } from "react";

// The assembled Node-M brand mark — the exact logo the loading intro draws.
// Shared verbatim by the loading splash (animated) and the signed-out screen
// (static) so the two brand moments can never drift apart. Paths lifted from
// the handoff's Loading Screen.dc.html.
const PEN_ROUTE = "M 20.77,174 L 20.77,2 L 94.5,92 L 168.38,2 L 168.38,174";
const M_OUTLINE =
  "M 0,0 H 55.78 L 94.473,65.428 133.421,0 H 189.092 V 170 H 147.666 V 45.652 L 108.5,111.772 H 80.701 L 41.535,45.652 V 170 H 0 Z";
const MAP_TEXT =
  "M 6.399501,162.77844 V 147.47476 L 21.38375,136.85865 6.399501,126.173 V 110.89963 H 34.399501 V 122.26461 H 13.918654 L 29.016902,133.01089 V 140.63776 L 13.918654,151.38315 H 34.399501 V 162.77844 Z M 29.298958,70.81818 V 89.621043 L 34.399501,92.589773 V 104.68491 L 6.399501,87.411883 V 73.067463 L 34.399501,55.79444 V 67.87976 Z M 24.102077,86.622003 V 73.847543 L 12.964376,80.224523 Z M 6.399501,49.60557 V 29.65444 Q 6.399501,20.747352 8.775829,15.989357 11.146271,11.221556 15.532844,11.221556 19.943502,11.221556 22.313944,15.989357 24.684385,20.747352 24.684385,29.65444 V 37.58087 H 34.399501 V 49.60557 Z M 11.63224,37.58087 H 19.45111 V 30.933758 Q 19.45111,27.434577 18.431002,25.525852 17.410893,23.616235 15.532844,23.616235 13.660682,23.616235 12.646462,25.525852 11.63224,27.434577 11.63224,30.933758 Z";
const MIND_TEXT =
  "M 182.24212,11.221557 V 23.766902 L 167.2579,32.46805 182.24212,41.22804 V 53.74842 H 154.24212 V 44.43124 H 174.7232 L 159.62473,35.62311 V 29.37094 L 174.7232,20.562811 H 154.24212 V 11.221557 Z M 182.24212,63.40705 V 73.263603 H 154.24212 V 63.40705 Z M 182.24212,82.918663 V 93.922583 L 163.0394,107.82837 H 182.24212 V 117.17676 H 154.24212 V 106.16482 L 173.44484,92.259923 H 154.24212 V 82.918663 Z M 176.78116,136.6643 H 159.69653 V 140.19558 Q 159.69653,146.23467 161.89294,149.42272 164.08928,152.61789 168.27179,152.61789 172.43086,152.61789 174.60307,149.43876 176.78116,146.26766 176.78116,140.19558 Z M 182.24212,126.80686 V 137.19742 Q 182.24212,145.90749 181.33002,150.16802 180.42373,154.42855 178.24563,157.47663 176.34899,160.16453 173.8712,161.46703 171.39863,162.77844 168.27179,162.77844 165.09738,162.77844 162.61304,161.46703 160.12878,160.16453 158.23861,157.47663 156.06051,154.40448 155.14774,150.10205 154.24212,145.80051 154.24212,137.19742 V 126.80686 Z";

/**
 * The brand mark. Without `animated` it renders in its final assembled state
 * (the signed-out screen); with `animated` the CSS intro on
 * `.mmm-brand-screen--animated` draws it in (the loading splash).
 */
export function BrandMark() {
  // Unique clip/mask ids per instance — the splash overlay and the signed-out
  // route can both be mounted during the splash's fade-out, so shared ids
  // would collide.
  const uid = useId().replace(/:/g, "");
  const clipId = `mmm-brand-m-clip-${uid}`;
  const maskId = `mmm-brand-m-mask-${uid}`;

  return (
    <div className="mmm-brand-logo">
      <svg viewBox="0 0 189.092 170">
        <defs>
          <clipPath id={clipId}>
            <path d={M_OUTLINE} />
          </clipPath>
          <mask id={maskId} maskUnits="userSpaceOnUse" x="-40" y="-40" width="270" height="250">
            <path
              className="mmm-brand-pen"
              d={PEN_ROUTE}
              fill="none"
              stroke="#ffffff"
              strokeWidth="60"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </mask>
        </defs>

        {/* Block M: a clipped pen-stroke — bottom-left up, diagonal down, diagonal up, straight down */}
        <g clipPath={`url(#${clipId})`}>
          <path
            className="mmm-brand-pen"
            d={PEN_ROUTE}
            fill="none"
            stroke="var(--logo-ink)"
            strokeWidth="60"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>

        {/* MAP (left leg): rises bottom-to-top in the M's ink color */}
        <g className="mmm-brand-map">
          <path d={MAP_TEXT} fill="var(--logo-ink)" fillRule="nonzero" />
        </g>

        {/* MAP recolor: a white copy revealed exactly where the M stroke has
            drawn, so ink→text happens bit by bit as the M sweeps over it */}
        <path d={MAP_TEXT} fill="var(--logo-text)" fillRule="nonzero" mask={`url(#${maskId})`} />

        {/* MIND (right leg): drops in top-to-bottom */}
        <g className="mmm-brand-mind">
          <path d={MIND_TEXT} fill="var(--logo-text)" fillRule="nonzero" />
        </g>

        {/* Gold Y: branch lines draw out from the hub, all at once */}
        <g fill="none" stroke="#c8902a" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round">
          <path className="mmm-brand-arm-long" d="M 94.546,98.5 L 48.546,22" />
          <path className="mmm-brand-arm-long" d="M 94.546,98.5 L 140.546,22" />
          <path className="mmm-brand-arm-short" d="M 94.546,98.5 L 94.546,155" />
        </g>

        {/* Gold nodes: hub grows first, then the three edge nodes together */}
        <g fill="#c8902a">
          <circle className="mmm-brand-node mmm-brand-node--hub" cx="94.546" cy="98.5" r="24.068" />
          <circle className="mmm-brand-node mmm-brand-node--edge" cx="48.546" cy="22" r="15" />
          <circle className="mmm-brand-node mmm-brand-node--edge" cx="140.546" cy="22" r="15" />
          <circle className="mmm-brand-node mmm-brand-node--edge" cx="94.546" cy="155" r="15" />
        </g>
      </svg>
    </div>
  );
}
